/**
 * DeclineFeedbackDialog Component
 *
 * Dialog shown after a candidate declines an offer.
 * Allows selecting a reason, optional input details, and free text.
 */
import { yupResolver } from '@hookform/resolvers/yup';
import { declineFeedbackFormDataPayloadToApiPayload } from 'features/feedback/helpers';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Dialog from 'shared/components/layout/dialog/Dialog';
import { useTenantContext } from 'shared/contexts';

import {
    DeclineFeedbackFormData,
    SendMessageRequest
} from '../../types';
import ReasonInputForm from './ReasonInputForm';
import ReasonSelector from './ReasonSelector';
import declineFeedbackValidationSchema from './validationSchema';

export interface DeclineFeedbackDialogProps {
    open: boolean;
    onClose: () => void;
    isLoading: boolean;
    error: Error | null;
    onSubmit: (data: SendMessageRequest['payload']) => void;
}

const defaultValues: DeclineFeedbackFormData = {
    primaryReason: 'other',
    freeText: '',
    reasonInput: null
};

export default function DeclineFeedbackDialog({
    open,
    onClose,
    onSubmit,
    isLoading,
    error
}: DeclineFeedbackDialogProps) {
    const { t } = useTranslation('feedback');
    const [ stage, setStage ] = useState<'selectReason' | 'setReasonDetails'>('selectReason');
    const form = useForm<DeclineFeedbackFormData>({
        defaultValues,
        resolver: yupResolver(declineFeedbackValidationSchema())
    });
    const timeZone = useTenantContext().timeZone;

    const { handleSubmit, reset } = form;

    const handleReasonSelect = () => {
        setStage('setReasonDetails');
    };

    const handleGoBackToReasonSelect = () => {
        setStage('selectReason');
    };

    const handleFormSubmit = (formData: DeclineFeedbackFormData) => {
        onSubmit(declineFeedbackFormDataPayloadToApiPayload(formData, timeZone));
    };

    useEffect(() => {
        if (open) {
            reset(defaultValues);
            setStage('selectReason');
        }
    }, [ open, reset ]);

    const errorText = error ? t('toast.error') : undefined;

    return (
        <FormProvider { ...form }>
            <Dialog variant="dialog"
                open={ open }
                onClose={ onClose }
                title={ t(`declineDialog.${ stage }.title`) }
                subtitle={ t(`declineDialog.${ stage }.subtitle`) }
            >
                { stage === 'selectReason'
                    ? <ReasonSelector onSelectReason={ handleReasonSelect } />
                    : <ReasonInputForm onGoBack={ handleGoBackToReasonSelect }
                        onSubmit={ handleSubmit(handleFormSubmit) }
                        isLoading={ isLoading }
                        error={ errorText } />
                }
            </Dialog>
        </FormProvider>
    );
}
